import { ENEMY_SPRITES, ENEMY_DIR_SPRITES, ENEMY_WALK_SPRITES } from '../rendering/CharacterSpriteSheets.js'
import { PLAYER_DAMAGE } from '../game/CombatBalance.js'

export class Enemy {
  constructor(game, x, y, id, type = 'zombie') {
    this.game = game
    this.id = id
    this.type = type
    this.x = x
    this.y = y
    this.velocity = { x: 0, y: 0 }
    this.facingDir = 'down' // 'up','down','left','right'
    this.facing = 0 // legacy radians for subclasses
    
    // Stats (override in subclasses)
    this.maxHealth = 50
    this.health = 50
    this.damage = PLAYER_DAMAGE.LIGHT
    this.moveSpeed = 2.5
    this.attackRange = 1.2
    this.attackCooldown = 1
    this.attackTimer = 0
    this.detectionRange = 10
    this.touchDamageCooldown = 0.6
    this.touchDamageTimer = 0
    
    // State
    this.isAlive = true
    this.isAttacking = false
    this.damageMultiplier = 1
    this.knockbackResistance = 0
    
    // AI
    this.state = 'idle'
    this.targetPosition = null
    this.wanderTimer = 0
    this.wanderInterval = 3
    
    // Collision
    this.radius = 0.4
    
    // Visual
    this.sprite = ENEMY_SPRITES[type] || ENEMY_SPRITES.zombie
    this.dirSprites = ENEMY_DIR_SPRITES[type] || null
    this.walkSprites = ENEMY_WALK_SPRITES[type] || null
    this.walkAnimTime = 0
    this.walkAnimSpeed = 6
    this.walkFrame = 0
    this.isMoving = false
    this.flashTimer = 0

    // Knockback (animated over time)
    this.knockbackVelocity = { x: 0, y: 0 }
    this.knockbackTimer = 0
    this.knockbackDuration = 0.14

    // Death sequence: finish immediately after the knockback completes.
    this.pendingDeath = false
  }
  
  update(delta) {
    if (!this.isAlive) return

    if (this.pendingDeath) {
      if (this.knockbackTimer > 0) {
        this.applyKnockback(delta)
      } else {
        this.finishDeath()
        return
      }

      if (this.flashTimer > 0) {
        this.flashTimer -= delta
      }
      return
    }

    if (this.applyKnockback(delta)) {
      this.updateAttack(delta)
      if (this.flashTimer > 0) {
        this.flashTimer -= delta
      }
      return
    }
    
    this.updateAI(delta)
    this.updateMovement(delta)
    this.updateAttack(delta)
    this.updateTouchDamage(delta)
    
    if (this.flashTimer > 0) {
      this.flashTimer -= delta
    }
  }

  applyKnockback(delta) {
    if (this.knockbackTimer <= 0) return false

    this.knockbackTimer -= delta
    const t = Math.max(this.knockbackTimer / this.knockbackDuration, 0)
    const moveX = this.knockbackVelocity.x * delta * Math.max(t, 0.25)
    const moveY = this.knockbackVelocity.y * delta * Math.max(t, 0.25)

    this.moveByWithCollision(moveX, moveY)

    if (this.knockbackTimer <= 0) {
      this.knockbackVelocity.x = 0
      this.knockbackVelocity.y = 0
      this.knockbackTimer = 0
    }

    return true
  }

  moveByWithCollision(dx, dy) {
    const maxStep = 0.08
    const steps = Math.max(1, Math.ceil(Math.max(Math.abs(dx), Math.abs(dy)) / maxStep))
    const stepX = dx / steps
    const stepY = dy / steps

    for (let i = 0; i < steps; i++) {
      const testX = this.x + stepX
      if (!this.collidesWithWorld(testX, this.y)) {
        this.x = testX
      }

      const testY = this.y + stepY
      if (!this.collidesWithWorld(this.x, testY)) {
        this.y = testY
      }
    }
  }
  
  updateAI(delta) {
    const player = this.game.player
    if (!player) return
    
    const dx = player.x - this.x
    const dy = player.y - this.y
    const distToPlayer = Math.sqrt(dx * dx + dy * dy)
    
    switch (this.state) {
      case 'idle':
        this.wanderTimer -= delta
        if (this.wanderTimer <= 0) {
          this.wanderTimer = this.wanderInterval + Math.random() * 2
          this.targetPosition = this.getWanderTarget()
        }
        
        if (distToPlayer < this.detectionRange) {
          this.state = 'chase'
        }
        break
        
      case 'chase':
        this.targetPosition = { x: player.x, y: player.y }
        
        if (distToPlayer > this.detectionRange * 1.5) {
          this.state = 'idle'
        } else if (distToPlayer < this.attackRange && this.hasLineOfSightTo(player.x, player.y)) {
          this.state = 'attack'
        }
        break
        
      case 'attack':
        this.targetPosition = null
        
        if (distToPlayer > this.attackRange * 1.5 || !this.hasLineOfSightTo(player.x, player.y)) {
          this.state = 'chase'
        }
        break
    }
  }
  
  updateMovement(delta) {
    if (!this.targetPosition) return
    
    const dx = this.targetPosition.x - this.x
    const dy = this.targetPosition.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    this.isMoving = false

    if (dist > 0.5) {
      // Normalize direction
      const dirX = dx / dist
      const dirY = dy / dist
      
      // Update facing (radians for legacy + direction string for sprites)
      this.facing = Math.atan2(dy, dx)
      if (Math.abs(dy) > Math.abs(dx)) {
        this.facingDir = dy < 0 ? 'up' : 'down'
      } else {
        this.facingDir = dx < 0 ? 'left' : 'right'
      }
      this.isMoving = true
      
      // Calculate new position
      const newX = this.x + dirX * this.moveSpeed * delta
      const newY = this.y + dirY * this.moveSpeed * delta
      
      // Collision with world (check all corners)
      if (!this.collidesWithWorld(newX, this.y)) {
        this.x = newX
      }
      if (!this.collidesWithWorld(this.x, newY)) {
        this.y = newY
      }
    }
  }
  
  updateAttack(delta) {
    this.attackTimer -= delta
    
    if (this.state === 'attack' && this.attackTimer <= 0) {
      this.attack()
      this.attackTimer = this.attackCooldown
    }
  }

  updateTouchDamage(delta) {
    const player = this.game.player
    if (!player) return

    this.touchDamageTimer -= delta
    if (this.touchDamageTimer > 0) return

    const dx = player.x - this.x
    const dy = player.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    const touchRange = this.radius + player.radius

    if (dist <= touchRange) {
      const actualDamage = this.damage * this.damageMultiplier
      player.takeDamage(actualDamage)
      this.touchDamageTimer = this.touchDamageCooldown
    }
  }
  
  attack() {
    const player = this.game.player
    if (!player) return
    
    const dx = player.x - this.x
    const dy = player.y - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    
    if (dist < this.attackRange && this.hasLineOfSightTo(player.x, player.y)) {
      const actualDamage = this.damage * this.damageMultiplier
      player.takeDamage(actualDamage)
    }
  }

  collidesWithWorld(x, y) {
    const r = this.radius
    return this.game.world.isSolid(x - r, y - r) ||
           this.game.world.isSolid(x + r, y - r) ||
           this.game.world.isSolid(x - r, y + r) ||
           this.game.world.isSolid(x + r, y + r)
  }

  hasLineOfSightTo(targetX, targetY) {
    const dx = targetX - this.x
    const dy = targetY - this.y
    const dist = Math.sqrt(dx * dx + dy * dy)
    if (dist <= 0.01) return true

    const step = 0.2
    const dirX = dx / dist
    const dirY = dy / dist
    const steps = Math.floor(dist / step)

    for (let i = 1; i < steps; i++) {
      const sampleX = this.x + dirX * i * step
      const sampleY = this.y + dirY * i * step
      if (this.game.world.isSolid(sampleX, sampleY)) {
        return false
      }
    }

    return true
  }
  
  takeDamage(amount) {
    if (this.pendingDeath || !this.isAlive) return 0

    const damage = Number.isFinite(amount) ? Math.max(0, amount) : 0
    if (damage <= 0) return 0
    this.health -= damage
    this.flashTimer = 0.1

    const player = this.game.player
    if (player) {
      const dx = this.x - player.x
      const dy = this.y - player.y
      const dist = Math.sqrt(dx * dx + dy * dy) || 1
      const knockbackForce = 1.5 * (1 - this.knockbackResistance)
      this.knockbackVelocity.x = (dx / dist) * (knockbackForce / this.knockbackDuration)
      this.knockbackVelocity.y = (dy / dist) * (knockbackForce / this.knockbackDuration)
      this.knockbackTimer = this.knockbackDuration
    }
    
    if (this.health <= 0) {
      this.health = 0
      this.pendingDeath = true
    }

    return damage
  }

  finishDeath() {
    this.pendingDeath = false
    this.die()
  }
  
  die() {
    if (!this.isAlive) return false
    this.isAlive = false
    this.pendingDeath = false
    this.game.enemyManager.markEnemyDead(this)
    this.dropLoot()
    this.game.itemManager?.spawnItem({
      type: 'coin',
      id: 'gold_coin',
      name: 'Gold Coin',
      count: 1
    }, { x: this.x, y: this.y })
    return true
  }
  
  dropLoot() {
    // Override in subclasses
  }
  
  getWanderTarget() {
    const angle = Math.random() * Math.PI * 2
    const distance = 3 + Math.random() * 5
    return {
      x: this.x + Math.cos(angle) * distance,
      y: this.y + Math.sin(angle) * distance
    }
  }
  
  render(renderer) {
    if (!this.isAlive) return

    // Flash white when damaged
    if (this.flashTimer > 0) {
      renderer.fillCircle(this.x, this.y, 0.5, '#fff')
      return
    }
    
    // Pick sprite based on direction + walk animation
    let sprite
    if (this.dirSprites) {
      if (this.isMoving && this.walkSprites) {
        this.walkAnimTime += 1/60 * this.walkAnimSpeed
        this.walkFrame = Math.floor(this.walkAnimTime) % 4
        const walkSet = this.walkSprites[this.facingDir] || this.walkSprites.down
        sprite = walkSet[this.walkFrame]
      } else {
        this.walkAnimTime = 0
        this.walkFrame = 0
        sprite = this.dirSprites[this.facingDir] || this.dirSprites.down
      }
    } else {
      // Fallback: old rotating sprite for enemy types without directional art
      sprite = this.sprite
    }

    // Draw sprite (no rotation — direction is baked into the sprite)
    renderer.drawSprite(sprite, this.x - 0.5, this.y - 0.5, 0, 1)
    
    // Draw health bar
    if (this.health < this.maxHealth) {
      renderer.drawHealthBar(this.x, this.y - 0.7, this.health, this.maxHealth)
    }
  }
  
  distanceTo(x, y) {
    const dx = x - this.x
    const dy = y - this.y
    return Math.sqrt(dx * dx + dy * dy)
  }
  
  serialize() {
    return {
      id: this.id,
      type: this.type,
      x: this.x,
      y: this.y,
      health: this.health,
      isAlive: this.isAlive
    }
  }
  
  deserialize(data) {
    this.x = data.x
    this.y = data.y
    this.health = data.health
    this.isAlive = data.isAlive
  }
}
